import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { getProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Reveal, MaskLine } from "@/components/Reveal";

const HERO_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_fort-worth-style/artifacts/vot65fw7_IMG_6813.webp";
const EDITORIAL =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_fort-worth-style/artifacts/8a8i51kk_IMG_6814.webp";
const LOOK =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_fort-worth-style/artifacts/zbunyz76_IMG_6782.webp";

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0, 0.6]);

  useEffect(() => {
    getProducts({ featured: true }).then(setFeatured).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] grid md:grid-cols-2 border-b border-border">
        <div className="flex flex-col justify-center px-5 md:px-12 py-20 order-2 md:order-1 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="text-xs font-mono uppercase tracking-[0.35em] text-muted-foreground mb-6"
          >
            Fort Worth, Texas
          </motion.p>
          <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl uppercase leading-[0.82] tracking-tighter">
            <MaskLine index={0}>A Walk</MaskLine>
            <MaskLine index={1}>
              <span className="text-outline">To</span> Faith.
            </MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-8 font-mono text-sm text-muted-foreground max-w-md leading-relaxed"
          >
            Garments forged in the tension between darkness and devotion. Raw energy. Sacred
            intent. Worn on the long road toward the light.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            data-testid="showcase-announcement"
            className="mt-6 flex items-center gap-3 max-w-md"
          >
            <span className="h-px w-8 bg-[#5B2A86]" />
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#EAE6DF]">
              Headlining <span className="text-[#9D4EDD]">MaciasMajnun</span> at the Fort Worth
              Fashion Showcase — Mid-October 2027
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-button"
              className="group flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs font-mono uppercase tracking-[0.2em] hover:bg-[#5B2A86] hover:text-foreground transition-colors duration-300"
            >
              Enter the Collection
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/story"
              data-testid="hero-story-button"
              className="border border-border px-8 py-4 text-xs font-mono uppercase tracking-[0.2em] hover:border-foreground transition-colors"
            >
              The Manifesto
            </Link>
          </motion.div>
        </div>
        <div className="relative overflow-hidden order-1 md:order-2 min-h-[55vh] md:min-h-full">
          <motion.img
            src={HERO_IMG}
            alt="A walk to faith"
            style={{ y: imgY, scale: imgScale }}
            className="absolute inset-0 w-full h-full object-cover object-top brightness-110 contrast-105 will-change-transform"
          />
          <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-background" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/50 via-transparent to-transparent md:bg-gradient-to-r md:from-background/80" />
        </div>
      </section>

      {/* FEATURED */}
      <section className="px-5 md:px-10 py-24">
        <Reveal className="flex items-end justify-between mb-14">
          <h2 className="font-serif text-4xl sm:text-5xl uppercase tracking-tight">
            Runway Moments
            <span className="block mt-3 text-xs sm:text-sm font-mono normal-case tracking-[0.2em] text-muted-foreground">
              Glimpses from past shows and upcoming MAJNUN collections
            </span>
          </h2>
          <Link
            to="/shop"
            data-testid="view-all-link"
            className="hidden sm:flex text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground items-center gap-2 shrink-0"
          >
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
          {featured.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* EDITORIAL SPLIT */}
      <section className="grid md:grid-cols-12 border-y border-border overflow-hidden">
        <div className="md:col-span-7 overflow-hidden min-h-[65vh]">
          <motion.img
            src={EDITORIAL}
            alt="Editorial"
            initial={{ scale: 1.15 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="md:col-span-5 flex flex-col justify-center px-5 md:px-12 py-20 bg-secondary">
          <Reveal delay={0.05}>
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#9D4EDD] mb-6">
              SS / ACT II
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="font-serif text-4xl sm:text-5xl uppercase leading-none tracking-tight">
              Beauty in the
              <br />
              Struggle
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 font-mono text-sm text-muted-foreground leading-relaxed max-w-md">
              MAJNUN — the one consumed by sin, yet driven to the edge of madness in pursuit of the
              divine. Each piece is cut from that same tension. Fallen, but still reaching.
            </p>
          </Reveal>
          <Reveal delay={0.28}>
            <Link
              to="/story"
              data-testid="editorial-story-link"
              className="mt-10 inline-block border-b border-foreground pb-1 text-xs font-mono uppercase tracking-[0.2em] hover:text-[#9D4EDD] hover:border-[#9D4EDD] transition-colors"
            >
              Read the full manifesto
            </Link>
          </Reveal>
        </div>
      </section>

      {/* LOOKBOOK TEASER */}
      <section className="px-5 md:px-10 py-24 grid md:grid-cols-3 gap-5 items-center">
        <Reveal className="overflow-hidden aspect-[3/4]">
          <img src={LOOK} alt="Lookbook" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
        </Reveal>
        <div className="text-center py-10">
          <h2 className="font-serif text-5xl sm:text-6xl uppercase leading-none tracking-tighter">
            <MaskLineOnView text="Mad" i={0} />
            <MaskLineOnView text="For" i={1} />
            <MaskLineOnView text="The" i={2} />
            <MaskLineOnView text="Divine" i={3} />
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              A line built for the faithful & the fallen
            </p>
          </Reveal>
        </div>
        <Reveal className="overflow-hidden aspect-[3/4]" delay={0.1}>
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_fort-worth-style/artifacts/shoj8pid_IMG_6812.webp"
            alt="Lookbook"
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          />
        </Reveal>
      </section>
    </div>
  );
};

const MaskLineOnView = ({ text, i = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.85, delay: 0.1 * i, ease: [0.16, 1, 0.3, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

export default Home;
